import React, { useState } from "react";

// Lib
import Big from "big.js";
import { shallowEqual } from "react-redux";
import { packedValue } from "lib/calculator";
import { useAppSelector } from "lib/hooks/useDispatch";

// Components
import Button from "components/UiKit/Button";
import FieldText from "components/UiKit/FieldText";
import FieldSelect from "components/UiKit/FieldSelect";

// Constants
import { styleFieldSelectPrices } from "constans/search";
import { optionsConcentration, optionsMass, optionsVolume } from "constans/molecule-product";

// Type
type TypeObject = {
  exp?: number,
  value?: number | string,
}
type TypeSelect = {
  label?: string,
  value?: number,
}
type TypeArgs = {
  mass?: TypeObject;
  volume?: TypeObject;
  molecularWeight?: number;
  concentration?: TypeObject;
}

const MolarityCalculator = () => {

  const molecularWeight = useAppSelector(
    (store) => store.moleculeProduct.data?.descriptor?.molecularWeight,
    shallowEqual
  );

  const [mass, setMass] = useState<string>("");
  const [volume, setVolume] = useState<string>("");
  const [concentration, setConcentration] = useState<string>("");

  const [massUnit, setMassUnit] = useState<number>(optionsMass[2].value);
  const [volumeUnit, setVolumeUnit] = useState<number>(optionsVolume[1].value);
  const [concentrationUnit, setConcentrationUnit] = useState<number>(optionsConcentration[1].value);

  const [errorMessage, setErrorMessage] = useState<string>("");

  const headlerReset = () => {
    setMass("");
    setVolume("");
    setConcentration("");

    setMassUnit(optionsMass[2].value);
    setVolumeUnit(optionsVolume[1].value);
    setConcentrationUnit(optionsConcentration[1].value);
  };

  const handleCalculate = () => {
    try {
      calculateMolarity({
        mass: { value: +mass, exp: massUnit },
        concentration: { value: +concentration, exp: concentrationUnit },
        volume: { value: +volume, exp: volumeUnit },
        molecularWeight
      });
    } catch (e: any) {
      setErrorMessage(e.message);
      return;
    }

    setErrorMessage("");
  };

  const calculateMolarity = ({ mass, concentration, volume, molecularWeight }: TypeArgs) => {
    let emptyNumber = 0;
    let invalidNumber = 0;

    if (mass?.value && (isNaN(+mass?.value) || +mass.value <= 0)) {
      invalidNumber += 1;
    } else if (!mass?.value) {
      emptyNumber += 1;
    }

    if (concentration?.value && (isNaN(+concentration?.value) || +concentration?.value <= 0)) {
      invalidNumber += 1;
    } else if (!concentration?.value) {
      emptyNumber += 1;
    }

    if (volume?.value && (isNaN(+volume?.value) || +volume?.value <= 0)) {
      invalidNumber += 1;
    } else if (!volume?.value) {
      emptyNumber += 1;
    }

    if (!molecularWeight || (isNaN(molecularWeight) || +molecularWeight <= 0)) {
      invalidNumber += 1;
      throw new Error("Please enter a valid Molecular Weight.");
    }

    if (emptyNumber > 1) {
      throw new Error("Please fill in three blanks.");
    }

    if (emptyNumber === 0 && invalidNumber >= 1) {
      throw new Error("Please leave one item blank.");
    }

    if (invalidNumber) {
      throw new Error("Please enter numbers only.");
    }

    const mwScale = 1e6;
    let bigResult: Big.Big | number = 0;

    if (!mass?.value && concentration?.value && volume?.value) {
      bigResult = new Big(concentration.value).times(Math.pow(10, (concentration.exp || 0))).times(volume.value).times(Math.pow(10, (volume.exp || 0))).times(molecularWeight).div(mwScale);
      const resultPackedValue = packedValue({ bigValue: bigResult, maxExp: 15, minExp: 0 });

      setMass(resultPackedValue.value);
      setMassUnit(resultPackedValue.exp);
    } else if (mass?.value && !concentration?.value && volume?.value) {
      bigResult = new Big(mass.value).times(Math.pow(10, (mass.exp || 0))).div(volume.value).div(Math.pow(10, (volume.exp || 0))).div(molecularWeight).times(mwScale);
      const resultPackedValue = packedValue({ bigValue: bigResult, maxExp: 12, minExp: 0 });

      setConcentration(resultPackedValue.value);
      setConcentrationUnit(resultPackedValue.exp);
    } else if (mass?.value && concentration?.value && !volume?.value) {
      bigResult = new Big(mass.value).times(Math.pow(10, (mass?.exp || 0))).div((concentration?.value || 0)).div(Math.pow(10, (concentration?.exp || 0))).div(molecularWeight).times(mwScale);
      const resultPackedValue = packedValue({ bigValue: bigResult, maxExp: 6, minExp: 0 });

      setVolume(resultPackedValue.value);
      setVolumeUnit(resultPackedValue.exp);
    } else {
      throw new Error("Please leave one item blank.");
    }
  };

  return (
    <div className="molarity-calculator flex flex-direction-column">
      <div className="flex flex-ai-center flex-jc-between mb30 full-width">
        <div>
          <h4 className="molarity-calculator__title mt20 mb20">The molarity calculator equation</h4>
          <p className="molarity-calculator__sub-title">Mass (g) = Concentration (mol/L) × Volume (L) × Molecular
            Weight
            (g/mol)</p>
        </div>

        <div className="flex flex-direction-column">
          <Button color="green" variable="primary" label="Calculate" onPress={handleCalculate}
                  className="molarity-calculator__submit mb10" />
          <Button variable="outline" label="Reset" onPress={headlerReset}
                  className="molarity-calculator__submit-clear" />
        </div>
      </div>
      <div className="flex flex-ai-start flex-direction-column">
        <div className="flex flex-ai-end flex-jc-between full-width mb30">
          <div className="flex flex-ai-end">
            <FieldText
              name="mass"
              label="Mass"
              value={mass}
              placeholder="Your value"
              className="molarity-calculator__input mr10"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMass(e.target.value)}
            />
            <FieldSelect
              name="selectMass"
              placeholder="Select"
              options={optionsMass}
              styles={styleFieldSelectPrices}
              onChange={(opt: TypeSelect) => setMassUnit(+(opt?.value || 0))}
              value={optionsMass.find((item) => item.value === massUnit)}
            />
          </div>
          <span className="molarity-calculator__sign mb10 mr5 ml5">=</span>
          <div className="flex flex-ai-end">
            <FieldText
              name="concentration"
              label="Concentration"
              value={concentration}
              placeholder="Your value"
              className="molarity-calculator__input mr10"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConcentration(e.target.value)}
            />
            <FieldSelect
              placeholder="Select"
              name="selectConcentration"
              options={optionsConcentration}
              styles={styleFieldSelectPrices}
              onChange={(opt: TypeSelect) => setConcentrationUnit(+(opt.value || 0))}
              value={optionsConcentration.find((item) => item.value === concentrationUnit)}
            />
          </div>
          <span className="molarity-calculator__sign mb10 mr5 ml5">×</span>
          <div className="flex flex-ai-end">
            <FieldText
              name="volume"
              label="Volume"
              value={volume}
              placeholder="Your value"
              className="molarity-calculator__input mr10"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVolume(e.target.value)}
            />
            <FieldSelect
              name="selectVolume"
              placeholder="Select"
              options={optionsVolume}
              styles={styleFieldSelectPrices}
              onChange={(opt: TypeSelect) => setVolumeUnit(+(opt.value || 0))}
              value={optionsVolume.find((item) => item.value === volumeUnit)}
            />
          </div>
          <span className="molarity-calculator__sign mb10 mr5 ml5">×</span>
          <FieldText
            label="MW"
            onChange={() => {
            }}
            name="molecularWeight"
            value={molecularWeight}
            placeholder="Your value"
            className="molarity-calculator__input mr10"
          />
        </div>

        {errorMessage && <div className="molarity-calculator__error-message">{errorMessage}</div>}
      </div>
    </div>
  );
};

export default MolarityCalculator;
